// Membership in the account dialog (docs/MEMBERSHIP_PLANS_DESIGN.md): the
// plan and credits an account has, and buying plans, the World Editor and
// credits through Stripe's checkout (web/server/lib/billing.mjs). It shows
// only when the server sells them.
//
// Someone without an account buys first: Stripe sends them back to
// ?purchase=<checkout>, and once its payment arrives the server answers a
// join code, with which the account dialog makes their account
// (account.js), and the account receives what was bought.

const PLAN_TEXT = {
  mini: "50 Blip credits a week, min effort, 2 GB of online storage",
  max: "200 Blip credits a week, min to max effort, 10 GB of online storage",
  ultra: "1,500 Blip credits a week, every effort, 50 GB of online storage, and the World Editor's MCP connection",
};
// Bytes as the account dialog shows them.
const size = (bytes) => bytes >= 1 << 30 ? `${Math.round(bytes / (1 << 30) * 10) / 10} GB` :
  `${Math.max(bytes ? 1 : 0, Math.round(bytes / (1 << 20)))} MB`;
const EUROZONE = new Set(["AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT", "LT", "LU", "LV", "MT",
  "NL", "PT", "SI", "SK"]);

// The currency to show prices in, from the browser's region. Stripe's
// checkout shows the buyer's own total.
function currencyFor(language = navigator.language || "") {
  const region = (language.split("-")[1] || "").toUpperCase();
  if (region === "GB") return "gbp";
  return EUROZONE.has(region) ? "eur" : "usd";
}

export function setupMembership({ cloud, $, status, notice, showAccount, setJoinCode }) {
  let offer = null;   // GET /api/billing, once
  let purchased = false;   // back from a purchase, to make the account

  const available = () => !!(cloud && cloud.config && cloud.config.billing);

  async function items() {
    if (!offer) offer = await cloud.request("GET", "billing").catch(() => ({ enabled: false }));
    return offer.enabled ? offer.items : {};
  }

  function price(item, currency) {
    const amounts = item.amounts || {};
    const chosen = amounts[currency] !== undefined ? currency : "usd";
    if (amounts[chosen] === undefined) return "";
    const text = new Intl.NumberFormat(undefined, { style: "currency", currency: chosen.toUpperCase() })
      .format(amounts[chosen] / 100);
    return item.taxIncluded && item.taxIncluded[chosen] ? text : `${text} + tax`;
  }

  async function buy(key, button) {
    button.disabled = true;
    try {
      const { url } = await cloud.request("POST", "billing/checkout", { json: { item: key } });
      location.assign(url);
    } catch (error) {
      button.disabled = false;
      $("account-error").textContent = error.message || String(error);
    }
  }

  // Buttons for some of the items on sale.
  async function buttons(container, keys) {
    const sale = await items();
    const currency = currencyFor();
    container.replaceChildren();
    for (const key of keys) {
      const item = sale[key];
      if (!item) continue;
      const label = item.kind === "plan" ?
        `${item.plan[0].toUpperCase()}${item.plan.slice(1)}, ${price(item, currency)} a ${item.interval}` :
        item.kind === "editor" ? `World Editor, ${price(item, currency)}` :
          `${item.credits} credits, ${price(item, currency)}`;
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = label;
      if (item.kind === "plan") button.title = PLAN_TEXT[item.plan] || "";
      button.addEventListener("click", () => buy(key, button));
      container.append(button);
    }
    return container.childElementCount > 0;
  }

  const PLANS = ["mini_month", "mini_year", "max_month", "max_year", "ultra_month"];
  // Credit packs are for members (billing.mjs).
  const CREDITS = ["credits_100", "credits_500", "credits_1000"];

  // The dialog's membership parts, for a signed-out or signed-in browser.
  async function render(signedIn) {
    $("account-join").hidden = true;
    $("account-membership").hidden = true;
    $("account-thanks").hidden = !(purchased && !signedIn);
    if (!available()) return;
    if (!signedIn && purchased) return;
    if (!signedIn) {
      $("account-join").hidden = !(await buttons($("account-join-buy"), [...PLANS, "world_editor"]));
      $("account-closed").hidden = true;
      return;
    }
    const me = await cloud.request("GET", "me").catch(() => ({}));
    const plan = me.membership;
    if (!plan) return;
    const lines = [];
    if (plan.plan) {
      const until = plan.until ? new Date(plan.until).toLocaleDateString() : "";
      const refill = new Date(plan.week.renews).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
      lines.push(`${plan.name}${plan.interval ? `, ${plan.interval}ly` : ""}${until ? (plan.renews ? `, renews by ${until}` :
        `, until ${until}`) : ""}. ${plan.week.left} of ${plan.week.credits} weekly credits left; refills ${refill}.`);
    } else {
      lines.push(plan.lapsed ? "Your membership has ended." : "You aren't a member yet.");
    }
    if (plan.bought.credits > 0) {
      lines.push(`${plan.bought.credits} bought credits${plan.bought.usable ? "" : ", used while you're a member"}.`);
    }
    if (plan.editor) lines.push("You own the World Editor.");
    if (plan.storage) {
      lines.push(`${size(plan.storage.used)} of ${size(plan.storage.account)} of online storage used.`);
    }
    $("membership-summary").textContent = lines.join(" ");
    await buttons($("membership-buy"), [...(plan.plan ? [] : PLANS), ...(plan.editor ? [] : ["world_editor"]),
      ...(plan.plan ? CREDITS : [])]);
    $("account-membership").hidden = false;
  }

  // Back from Stripe's checkout: wait for its payment to arrive.
  async function handleReturn() {
    const params = new URLSearchParams(location.search);
    const session = params.get("purchase");
    if (!session || !cloud) return;
    params.delete("purchase");
    history.replaceState(null, "", `${location.pathname}${params.size ? `?${params}` : ""}${location.hash}`);
    if (session === "cancelled") {
      status("The purchase was cancelled. Nothing was charged.");
      return;
    }
    status("Waiting for your payment…");
    for (let tries = 0; tries < 40; tries++) {
      const outcome = await cloud.request("GET", `billing/sessions/${encodeURIComponent(session)}`).catch(() => null);
      if (outcome && outcome.join) {
        setJoinCode(outcome.join);
        purchased = true;
        status("Thank you! Now make your account: what you bought comes with it.");
        showAccount();
        return;
      }
      if (outcome && (outcome.status === "paid" || outcome.status === "claimed")) {
        status("Thank you! Your purchase is in your account.");
        showAccount();
        return;
      }
      if (!outcome) break;
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    notice("Your payment is still being processed. It will appear in your account soon: reload this page to see it.",
      [], "cloud");
  }

  return { render, handleReturn };
}
